import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { logError } from '@/lib/errorHandling';

interface UserCredits {
  balance: number;
  total_deposited: number;
  total_spent: number;
}

export function useCredits() {
  const { user } = useAuth();
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [purchasedPredictions, setPurchasedPredictions] = useState<Set<string>>(new Set());
  const [unlockedMatches, setUnlockedMatches] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setCredits(null);
      setPurchasedPredictions(new Set());
      setUnlockedMatches(new Set());
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch user credits
      const { data: creditsData, error: creditsError } = await supabase
        .from('user_credits')
        .select('balance, total_deposited, total_spent')
        .eq('user_id', user.id)
        .maybeSingle();

      if (creditsError) {
        logError('useCredits:fetchCredits', creditsError, { userId: user.id });
        throw creditsError;
      }

      if (creditsData) {
        setCredits(creditsData);
      } else {
        // Create credits record if it doesn't exist (for existing users)
        const { data: newCredits, error: insertError } = await supabase
          .from('user_credits')
          .insert({ user_id: user.id, balance: 0 })
          .select('balance, total_deposited, total_spent')
          .single();
        
        if (insertError) {
          logError('useCredits:createCredits', insertError, { userId: user.id });
          throw insertError;
        }
        
        if (newCredits) {
          setCredits(newCredits);
        }
      }

      // Fetch purchased predictions (legacy)
      const { data: purchasedData, error: purchaseError } = await supabase
        .from('purchased_predictions')
        .select('prediction_id')
        .eq('user_id', user.id);

      if (purchaseError) {
        logError('useCredits:fetchPurchases', purchaseError, { userId: user.id });
      }

      if (purchasedData) {
        setPurchasedPredictions(new Set(purchasedData.map(p => p.prediction_id)));
      }

      // Fetch unlocked matches (new system)
      const { data: unlockedData, error: unlockedError } = await supabase
        .from('unlocked_matches')
        .select('match_id')
        .eq('user_id', user.id);

      if (unlockedError) {
        logError('useCredits:fetchUnlockedMatches', unlockedError, { userId: user.id });
      }

      if (unlockedData) {
        setUnlockedMatches(new Set(unlockedData.map(u => u.match_id)));
      }
    } catch (err) {
      setError('Failed to load credits. Please try again.');
      logError('useCredits', err, { userId: user.id });
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Legacy: purchase prediction by ID
  const purchasePrediction = async (predictionId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.rpc('purchase_prediction', {
        p_prediction_id: predictionId
      });

      if (error) {
        logError('useCredits:purchase', error, { predictionId, userId: user.id });
        throw new Error(error.message);
      }

      await fetchCredits();
      return true;
    } catch (err) {
      logError('useCredits:purchase', err, { predictionId });
      throw err;
    }
  };

  // NEW: Unlock match (centralized - works even without predictions)
  const unlockMatch = async (matchId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.rpc('unlock_match', {
        p_match_id: matchId
      });

      if (error) {
        logError('useCredits:unlockMatch', error, { matchId, userId: user.id });
        throw new Error(error.message);
      }

      await fetchCredits();
      return true;
    } catch (err) {
      logError('useCredits:unlockMatch', err, { matchId });
      throw err;
    }
  };

  // Legacy check for specific prediction
  const hasPurchased = (predictionId: string): boolean => {
    return purchasedPredictions.has(predictionId);
  };

  // NEW: Check if match is unlocked (includes legacy purchased_predictions)
  const hasUnlockedMatch = (matchId: string): boolean => {
    return unlockedMatches.has(matchId);
  };

  // Combined check: match unlocked OR has legacy prediction purchase for this match
  const hasMatchAccess = async (matchId: string): Promise<boolean> => {
    if (!user) return false;
    
    // Check local state first
    if (unlockedMatches.has(matchId)) return true;
    
    // Check via RPC for real-time accuracy (includes legacy check)
    try {
      const { data, error } = await supabase.rpc('has_unlocked_match', {
        p_match_id: matchId
      });
      return data === true;
    } catch {
      return false;
    }
  };

  return {
    credits,
    isLoading,
    error,
    purchasePrediction,
    unlockMatch,
    hasPurchased,
    hasUnlockedMatch,
    hasMatchAccess,
    refetch: fetchCredits,
  };
}
